import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getApiErrorStatus } from '../../services/marketplace/errors';
import { useGetFeedbackToken } from '../../services/marketplace/generated/kubb/hooks/useGetFeedbackToken';
import { usePostFeedbackTokenAnswers } from '../../services/marketplace/generated/kubb/hooks/usePostFeedbackTokenAnswers';
import { VoiceAnswer } from './VoiceQuestion';

export type SurveyPhase = 'intro' | 'questions' | 'done';

const useFeedbackSurvey = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token') ?? '';

  // No background refetches: a refetch after the last answer saves returns
  // state "completed" and would swap the thank-you screen for the
  // already-answered card mid-interaction.
  const validation = useGetFeedbackToken(
    { token },
    {
      query: {
        enabled: !!token,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    },
  );

  // A 404 means the feature is off or the token can't exist — same driver
  // experience as the API's own invalid state, so fold it in.
  const invite =
    validation.data ??
    (getApiErrorStatus(validation.error) === 404
      ? ({ state: 'invalid', firstName: null } as const)
      : undefined);
  const activeInvite = invite?.state === 'active' ? invite : null;
  const questions = activeInvite?.questions ?? [];

  const [phase, setPhase] = useState<SurveyPhase>('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const resumedRef = useRef(false);

  useEffect(() => {
    if (!activeInvite || resumedRef.current) return;
    resumedRef.current = true;
    const savedKeys = new Set(activeInvite.answers.map((answer) => answer.questionKey));
    if (savedKeys.size === 0) return;
    const firstUnanswered = activeInvite.questions.findIndex(
      (question) => !savedKeys.has(question.key),
    );
    if (firstUnanswered === -1) {
      setPhase('done');
    } else {
      setQuestionIndex(firstUnanswered);
      setPhase('questions');
    }
  }, [activeInvite]);

  const save = usePostFeedbackTokenAnswers();

  const saveAnswer = (answer: VoiceAnswer) => {
    const question = questions[questionIndex];
    if (!question || save.isPending) return;
    save.mutate(
      {
        token,
        data: {
          questionKey: question.key,
          ...(answer.kind === 'voice' ? { audio: answer.blob } : { text: answer.text }),
          rating: answer.rating,
        },
      },
      {
        onSuccess: () => {
          // Forward-only flow over a contiguous answered prefix: the next
          // unanswered question is always simply the next one.
          if (questionIndex + 1 < questions.length) {
            setQuestionIndex(questionIndex + 1);
          } else {
            setPhase('done');
          }
        },
        onError: (error) => {
          // 409 = invite no longer active (completed in another tab, or
          // expired mid-session); retrying the save can never succeed, so
          // revalidate and let the matching link-state card take over.
          if (getApiErrorStatus(error) === 409) validation.refetch();
        },
      },
    );
  };

  return {
    token,
    invite,
    loadFailed: validation.isError && !invite,
    retry: () => validation.refetch(),
    questions,
    firstName: invite?.firstName ?? null,
    isCompletedSale: activeInvite?.surveyGroup === 'completed_sale',
    phase,
    startSurvey: () => setPhase('questions'),
    questionIndex,
    saveAnswer,
    isSaving: save.isPending,
    sendFailed: save.isError,
  };
};

export default useFeedbackSurvey;
